import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MagnifyingGlass, X, ArrowRight, Briefcase, BookOpen, CalendarBlank, ShoppingBag, ChatCircleDots } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useCurrency } from "../lib/currency";

const groups = [
  { key: "therapists", label: "Therapists", icon: Briefcase, fetch: () => api.get("/therapists").then((r) => r.data), to: (i) => `/therapists/${i.id}`, line: (i) => i.title },
  { key: "mentors", label: "Mentors", icon: Briefcase, fetch: () => api.get("/mentors").then((r) => r.data), to: (i) => `/mentors/${i.id}`, line: (i) => i.title },
  { key: "events", label: "Events", icon: CalendarBlank, fetch: () => api.get("/events").then((r) => r.data), to: () => `/events`, line: (i) => i.date, titleKey: "title" },
  { key: "products", label: "Shop", icon: ShoppingBag, fetch: () => api.get("/products").then((r) => r.data), to: () => `/shop`, line: (i, fmt) => fmt(i.price) },
  { key: "articles", label: "Articles", icon: BookOpen, fetch: () => api.get("/articles").then((r) => r.data), to: (i) => `/articles/${i.slug || i.id}`, line: (i) => i.excerpt?.slice(0, 80) },
];

export default function SearchModal({ onClose }) {
  const { format } = useCurrency();
  const [q, setQ] = useState("");
  const [pools, setPools] = useState({});
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    let mounted = true;
    Promise.all(groups.map((g) => g.fetch().catch(() => []))).then((arr) => {
      if (!mounted) return;
      const map = {};
      groups.forEach((g, i) => { map[g.key] = arr[i] || []; });
      setPools(map);
    });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const results = useMemo(() => {
    const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!words.length) return groups.map((g) => ({ ...g, items: (pools[g.key] || []).slice(0, 4) }));
    return groups.map((g) => ({
      ...g,
      // Every word must match somewhere in the record (AND across words),
      // so multi-word queries like "career mock interview" work.
      items: (pools[g.key] || []).filter((it) => {
        const haystack = JSON.stringify(it).toLowerCase();
        return words.every((w) => haystack.includes(w));
      }).slice(0, 6),
    }));
  }, [q, pools]);

  const totalHits = results.reduce((s, g) => s + g.items.length, 0);

  return (
    <div className="md-search-overlay" onClick={onClose} data-testid="search-modal">
      <div className="md-search-shell" onClick={(e) => e.stopPropagation()}>
        <div className="md-search-input-wrap">
          <MagnifyingGlass size={18} weight="bold" />
          <input ref={inputRef} placeholder="Search therapists, mentors, events, shop, articles…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="search-modal-input" />
          <kbd className="md-search-esc">esc</kbd>
          <button className="md-search-close" onClick={onClose} aria-label="Close"><X size={16} weight="bold" /></button>
        </div>
        <div className="md-search-results">
          {totalHits === 0 ? (
            <div className="md-search-empty">
              <ChatCircleDots size={28} weight="duotone" />
              <p>No matches for <em>“{q}”</em>. Try a different keyword.</p>
            </div>
          ) : (
            results.filter((g) => g.items.length > 0).map((g) => (
              <div key={g.key} className="md-search-group">
                <div className="md-search-group-head">
                  <g.icon size={14} weight="duotone" />
                  <span>{g.label}</span>
                  <span className="md-search-group-count">{g.items.length}</span>
                </div>
                {g.items.map((it) => (
                  <Link key={it.id || it.slug} to={g.to(it)} onClick={onClose} className="md-search-item" data-testid={`search-result-${g.key}-${it.id || it.slug}`}>
                    <div className="md-search-item-text">
                      <div className="md-search-item-title">{it.name || it.title}</div>
                      <div className="md-search-item-sub">{g.line(it, format) || ""}</div>
                    </div>
                    <ArrowRight size={14} weight="bold" />
                  </Link>
                ))}
              </div>
            ))
          )}
        </div>
        <div className="md-search-foot">
          <span><kbd>↑</kbd><kbd>↓</kbd> navigate</span>
          <span><kbd>↵</kbd> open</span>
          <span><kbd>esc</kbd> close</span>
        </div>
      </div>
    </div>
  );
}
